import { GetPayLinkResponse } from '@iwoca/lapi-client/edge';
import classNames from 'classnames';

import styles from './Pagination.module.css';
import { ReactComponent as Chevron } from '../../../../components/svg/Chevron.svg';

export const Pagination = ({
  onPageSelect,
  currentPageNumber,
  paginationLinks,
}: {
  onPageSelect: (page?: number) => void;
  currentPageNumber: number;
  paginationLinks: GetPayLinkResponse['links'] | undefined;
}) => {
  const previousPageLink = paginationLinks?.previous;
  const nextPageLink = paginationLinks?.next;
  const previousPageNumber = getPageNumber(previousPageLink);
  const nextPageNumber = getPageNumber(nextPageLink);

  const previousPageAction = () => onPageSelect(previousPageNumber);
  const nextPageAction = () => onPageSelect(nextPageNumber);

  const pageNumbers = getPages(paginationLinks);

  return (
    <div className={styles.pages}>
      {previousPageAction && (
        <button onClick={previousPageAction} className={styles.previousButton}>
          <Chevron className={styles.previousChevron} title="Previous button" />
        </button>
      )}
      {pageNumbers?.map((pageNumber) => {
        return (
          <PageNumber
            key={pageNumber}
            pageNumber={pageNumber}
            isSelected={currentPageNumber === pageNumber}
            onClick={() => onPageSelect(pageNumber)}
          />
        );
      })}
      {nextPageAction && (
        <button onClick={nextPageAction} className={styles.nextButton}>
          <Chevron className={styles.nextChevron} title="Next button" />
        </button>
      )}
    </div>
  );
};

export const PageNumber = ({
  pageNumber,
  isSelected,
  onClick,
}: {
  onClick: () => void;
  pageNumber: number;
  isSelected: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      className={classNames(styles.pageNumber, {
        [styles.selectedPage]: isSelected,
      })}
      title={`Page ${pageNumber}`}
    >
      {pageNumber}
    </button>
  );
};

function getPageNumber(url?: string) {
  if (!url) return;

  const pageQuery = new URL(url).searchParams.get('page');

  if (!pageQuery) return 1;

  return parseInt(pageQuery);
}

function getPages(links?: GetPayLinkResponse['links']) {
  if (!links?.self || !links.last) {
    return [];
  }

  const maxPages = 5;
  const lastPage = getPageNumber(links.last)!;
  const currentPage = getPageNumber(links.self)!;

  let virtualArrayIndex = Math.floor(currentPage / maxPages); // The virtual array is an array of arrays which we calculate on the fly. As we're only given the current page and the last page we need to grab the user's 5 visible pages which the virtualArrayIndex indicates.

  if (currentPage % 5 === 0) {
    virtualArrayIndex -= 1;
  }

  const virtualPageNumber = virtualArrayIndex * 5; // using the index we're able to calculate where the pages in the array start
  const pages = [];
  for (let i = 0; i < maxPages; i++) {
    const pageNumber = i + virtualPageNumber;
    if (pageNumber + 1 > lastPage) {
      break;
    }
    pages[i] = pageNumber + 1;
  }

  return pages;
}
